import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Badge,
  Tooltip,
  Popper,
  Paper,
  Fade,
} from '@mui/material';
import BrandWordmark from '../components/BrandWordmark';
import AppUpdateBanner from '../components/AppUpdateBanner';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  ListAlt as ListAltIcon,
  Notifications as NotificationsIcon,
  ReportProblem as ReportProblemIcon,
  Build as BuildIcon,
  Support as SupportIcon,
  LocalShipping as LocalShippingIcon,
  MenuBook as DocumentationIcon,
  EditNote as EditNoteIcon,
  Campaign as CampaignIcon,
  DirectionsCar as DirectionsCarIcon,
  Speed as SpeedIcon,
  CalendarMonth as CalendarMonthIcon,
  Handshake as HandshakeIcon,
  CloudUpload as CloudUploadIcon,
  Map as MapIcon,
  History as HistoryIcon,
  Folder as FolderIcon,
  Category as CategoryIcon,
  Schedule as ScheduleIcon,
  Repeat as RepeatIcon,
  NotificationsActive as NotificationsActiveIcon,
  Groups as GroupsIcon,
  LocationOn as LocationOnIcon,
  Email as EmailIcon,
  Label as LabelIcon,
  CallReceived as CallReceivedIcon,
  CallMade as CallMadeIcon,
  Sync as SyncIcon,
  BrandingWatermark as BrandingIcon,
  FilterList as FilterListIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { renderAssetViewIcon } from '../constants/assetViewIcons';
import { renderLocationViewIcon } from '../constants/locationViewIcons';
import { type BusinessAssetView } from '../utils/assetFilters';
import { type BusinessLocationView } from '../utils/locationFilters';
import { useNavigate, Outlet, useLocation, Navigate } from 'react-router-dom';
import { styled, type Theme } from '@mui/material/styles';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import {
  parseAdminUiLabels,
  resolveAdminUiLabel,
  resolveTemplateTabLabel,
  type AdminUiLabelKey,
} from '../constants/adminUiLabels';
import { AdminUiLabelsProvider } from '../context/AdminUiLabelsContext';
import { locationLabel } from '../utils/terminology';

const drawerWidthExpanded = 260;

type SubItem = {
  icon: React.ReactNode;
  label: string;
  path: string;
};

type MenuEntry = {
  id: AdminUiLabelKey | string;
  icon: React.ReactNode;
  path: string;
  indent?: boolean;
  exactMatch?: boolean;
  subItems?: SubItem[];
  /** Raw label bypasses adminUiLabels resolution (used for dynamic views) */
  rawLabel?: string;
};

type NavSection = { title: string; items: MenuEntry[] };

const pageTabPath = (base: string, tab: string) => `${base}?tab=${tab}`;

const assetsTabPath = (tab: string) => pageTabPath('/admin/assets', tab);

const getAssetsSubItems = (allowKeyDocuments: boolean): SubItem[] => {
  const items: SubItem[] = [
    { icon: <BuildIcon />, label: 'Assets', path: assetsTabPath('assets') },
    { icon: <CategoryIcon />, label: 'Asset types', path: assetsTabPath('types') },
    { icon: <CloudUploadIcon />, label: 'Import', path: assetsTabPath('import') },
    { icon: <MapIcon />, label: 'Map', path: assetsTabPath('map') },
    { icon: <HistoryIcon />, label: 'Out of action audit', path: assetsTabPath('audit') },
  ];
  if (allowKeyDocuments) {
    items.push({ icon: <FolderIcon />, label: 'Files', path: assetsTabPath('files') });
  }
  return items;
};

const getLocationsSubItems = (locationsLabel: string, useSiteTerminology?: boolean): SubItem[] => [
  {
    icon: <LocationOnIcon />,
    label: locationsLabel,
    path: '/admin/locations',
  },
  {
    icon: <FolderIcon />,
    label: `${locationLabel(!!useSiteTerminology)} Files`,
    path: pageTabPath('/admin/locations', 'files'),
  },
  {
    icon: <CloudUploadIcon />,
    label: 'Import',
    path: pageTabPath('/admin/locations', 'import'),
  },
];

const getLocationChecksSubItems = (): SubItem[] => [
  { icon: <DashboardIcon />, label: 'Overview', path: pageTabPath('/admin/location-checks', 'overview') },
  { icon: <AssessmentIcon />, label: 'Responses', path: pageTabPath('/admin/location-checks', 'responses') },
];

const getCommunicationsSubItems = (): SubItem[] => [
  { icon: <DashboardIcon />, label: 'Overview', path: pageTabPath('/admin/communications', 'overview') },
  { icon: <ScheduleIcon />, label: 'Schedules', path: pageTabPath('/admin/communications', 'schedules') },
  { icon: <RepeatIcon />, label: 'Round-Ups', path: pageTabPath('/admin/communications', 'roundups') },
  { icon: <NotificationsActiveIcon />, label: 'Alerts', path: pageTabPath('/admin/communications', 'alerts') },
];

const getTemplatesSubItems = (
  allowLocationChecks?: boolean,
  useSiteTerminology?: boolean,
  adminUiLabels?: Partial<Record<AdminUiLabelKey, string>>,
): SubItem[] => {
  const labels = adminUiLabels ?? {};
  const items: SubItem[] = [
    { icon: <AssignmentIcon />, label: resolveTemplateTabLabel('nav_safety_checks', labels), path: pageTabPath('/admin/templates', 'safety') },
    { icon: <HandshakeIcon />, label: resolveTemplateTabLabel('nav_loan_checks', labels), path: pageTabPath('/admin/templates', 'loan') },
    { icon: <LocalShippingIcon />, label: resolveTemplateTabLabel('nav_delivery_checks', labels), path: pageTabPath('/admin/templates', 'delivery') },
  ];
  if (allowLocationChecks) {
    items.push({
      icon: <LocationOnIcon />,
      label: resolveTemplateTabLabel('nav_location_checks', labels),
      path: pageTabPath('/admin/templates', 'location'),
    });
  }
  return items;
};

const getSafetyChecksSubItems = (): SubItem[] => [
  { icon: <DashboardIcon />, label: 'Overview', path: pageTabPath('/admin/safety-checks', 'overview') },
  { icon: <AssessmentIcon />, label: 'Responses', path: pageTabPath('/admin/safety-checks', 'responses') },
  { icon: <EmailIcon />, label: 'Email (web)', path: pageTabPath('/admin/safety-checks', 'email-web') },
];

const getLoanChecksSubItems = (): SubItem[] => [
  { icon: <DashboardIcon />, label: 'Overview', path: pageTabPath('/admin/loan-checks', 'overview') },
  { icon: <AssessmentIcon />, label: 'Responses', path: pageTabPath('/admin/loan-checks', 'responses') },
];

const getDeliveryChecksSubItems = (): SubItem[] => [
  { icon: <CallReceivedIcon />, label: 'Incoming', path: pageTabPath('/admin/delivery-checks', 'incoming') },
  { icon: <CallMadeIcon />, label: 'Outgoing', path: pageTabPath('/admin/delivery-checks', 'outgoing') },
];

const getDvlaSubItems = (): SubItem[] => [
  { icon: <PeopleIcon />, label: 'Staff Management', path: pageTabPath('/admin/dvla', 'staff') },
  { icon: <AssignmentIcon />, label: 'License Requests', path: pageTabPath('/admin/dvla', 'requests') },
  { icon: <AssessmentIcon />, label: 'Analytics Dashboard', path: pageTabPath('/admin/dvla', 'analytics') },
  { icon: <NotificationsIcon />, label: 'Notifications', path: pageTabPath('/admin/dvla', 'notifications') },
  { icon: <SettingsIcon />, label: 'Settings', path: pageTabPath('/admin/dvla', 'settings') },
];

const getSettingsSubItems = (allowSharedAccounts: boolean, useSiteTerminology?: boolean): SubItem[] => {
  const items: SubItem[] = [
    { icon: <PeopleIcon />, label: 'Users', path: pageTabPath('/admin/settings', 'users') },
    { icon: <GroupsIcon />, label: 'Teams', path: pageTabPath('/admin/settings', 'teams') },
    { icon: <SecurityIcon />, label: 'Roles', path: pageTabPath('/admin/settings', 'roles') },
    { icon: <LabelIcon />, label: 'Tags', path: pageTabPath('/admin/settings', 'job-tags') },
    { icon: <EmailIcon />, label: 'Email Configuration', path: pageTabPath('/admin/settings', 'email') },
    { icon: <DashboardIcon />, label: 'Dashboard Settings', path: pageTabPath('/admin/settings', 'dashboard') },
    { icon: <SyncIcon />, label: 'Sync Conflicts', path: pageTabPath('/admin/settings', 'sync-conflicts') },
    { icon: <BrandingIcon />, label: 'Branding', path: pageTabPath('/admin/settings', 'branding') },
    { icon: <FilterListIcon />, label: 'Asset Views', path: pageTabPath('/admin/settings', 'asset-views') },
    { icon: <MapIcon />, label: 'Location Views', path: pageTabPath('/admin/settings', 'location-views') },
    {
      icon: <LocationOnIcon />,
      label: locationLabel(!!useSiteTerminology, { plural: true }),
      path: pageTabPath('/admin/settings', 'home-locations'),
    },
  ];
  if (allowSharedAccounts) {
    items.push({
      icon: <PersonIcon />,
      label: 'Shared Accounts',
      path: pageTabPath('/admin/settings', 'shared-accounts'),
    });
  }
  return items;
};

const drawerWidthCollapsed = 72;
const SIDEBAR_COLLAPSED_KEY = 'adminSidebarCollapsed';
const appBarHeight = 56;

const drawerTransition = (theme: Theme) =>
  theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  });

const navItemButtonSx = (theme: Theme) => ({
  borderRadius: 1.5,
  minHeight: 36,
  px: 1.5,
  fontFamily: 'Lato, sans-serif',
  fontSize: '0.845rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  textTransform: 'none' as const,
  '& .MuiSvgIcon-root': { fontSize: '1.15rem', color: theme.palette.text.secondary },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.primary.main,
    '& .MuiSvgIcon-root': { color: theme.palette.primary.main },
  },
});

function isNavPathActive(pathname: string, path: string, exact = false): boolean {
  if (exact) return pathname === path;
  if (pathname === path) return true;
  return pathname.startsWith(`${path}/`);
}

const decodeJWT = (token: string | null): Record<string, any> | null => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'drawerWidth' })<{
  drawerWidth: number;
}>(({ theme, drawerWidth }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: drawerTransition(theme),
  [theme.breakpoints.up('sm')]: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    maxWidth: `calc(100% - ${drawerWidth}px)`,
    boxSizing: 'border-box',
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
    maxWidth: '100%',
    marginTop: `${appBarHeight}px`,
    padding: theme.spacing(2.5),
  },
}));

const AdminLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const decodedToken = decodeJWT(localStorage.getItem('token'));
  const userDvlaAccessEnabled = decodedToken?.dvlaAccessEnabled === true || decodedToken?.isPlatformUser === true || decodedToken?.role === 'Super Admin' || decodedToken?.role === 'Owner';
  // Start with JWT-decoded value so nav is correct immediately, then keep updated live from API
  const [userRestrictions, setUserRestrictions] = useState<Record<string, boolean>>(
    decodedToken?.userModuleRestrictions ?? {}
  );
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true',
  );
  const navigate = useNavigate();
  const location = useLocation();
  const [businessName, setBusinessName] = useState<string>('');
  const [businessPermissions, setBusinessPermissions] = useState<{
    allowHrModule: boolean;
    allowIncidents: boolean;
    allowDvla: boolean;
    allowSafetyChecks: boolean;
    allowLoanChecks: boolean;
    allowDeliveryChecks: boolean;
    allowCalendar: boolean;
    allowMotTaxTab: boolean;
    allowVehicleEvaluationTab: boolean;
    hideMotTaxNavWhenDisabled: boolean;
    hideVehicleEvaluationNavWhenDisabled: boolean;
    hideTemplates?: boolean;
    allowKeyDocuments: boolean;
    allowSharedAccounts: boolean;
    allowLocationChecks: boolean;
    useSiteTerminology: boolean;
  } | null>(null); // Start as null to indicate loading state
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [dvlaNotificationCount, setDvlaNotificationCount] = useState(0);
  const [safetyChecksNotificationCount, setSafetyChecksNotificationCount] = useState(0);
  const [loanChecksNotificationCount, setLoanChecksNotificationCount] = useState(0);
  const [incidentsNotificationCount, setIncidentsNotificationCount] = useState(0);
  const [deliveryChecksNotificationCount, setDeliveryChecksNotificationCount] = useState(0);
  const [installationsNotificationCount, setInstallationsNotificationCount] = useState(0);
  const [hideSidebarNotifications, setHideSidebarNotifications] = useState(false);
  const [adminUiLabels, setAdminUiLabels] = useState<Partial<Record<AdminUiLabelKey, string>>>({});
  const [assetViews, setAssetViews] = useState<BusinessAssetView[]>([]);
  const [locationViews, setLocationViews] = useState<BusinessLocationView[]>([]);

  const [flyout, setFlyout] = useState<{
    anchorEl: HTMLElement;
    item: MenuEntry;
    activeSubIdx: number;
  } | null>(null);
  const hoverOpenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch live user restrictions from the API so changes made by admins take effect
  // without requiring a re-login. Runs on mount and whenever the tab regains focus.
  useEffect(() => {
    let cancelled = false;
    const fetchRestrictions = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API_BASE_URL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const r = res.data?.data?.userModuleRestrictions ?? res.data?.userModuleRestrictions ?? {};
        setUserRestrictions(r);
      } catch {
        // Silently fail — keep whatever value is already in state
      }
    };

    fetchRestrictions();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchRestrictions();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  useEffect(() => {
    // Load user preferences from API
    const loadUserPreferences = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await axios.get(`${API_BASE_URL}/users/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data?.success) {
          const preferences = response.data.preferences || {};
          const hideNotificationsValue = preferences.hideSidebarNotifications;
          // Handle various data types: true, 1, "1", "true", "True"
          const hideNotifications = hideNotificationsValue === true || 
                                     hideNotificationsValue === 1 || 
                                     hideNotificationsValue === '1' || 
                                     hideNotificationsValue === 'true' ||
                                     hideNotificationsValue === 'True';
          setHideSidebarNotifications(hideNotifications);
        }
      } catch (err) {
        console.error('Error loading user preferences:', err);
      }
    };

    loadUserPreferences();

    // Listen for preference changes
    const handlePreferenceChange = (event: CustomEvent) => {
      setHideSidebarNotifications(event.detail.hide);
    };

    window.addEventListener('sidebarNotificationsPreferenceChanged', handlePreferenceChange as EventListener);

    // Reload business (and asset views) when the settings panel saves new views
    const handleViewsChanged = () => { fetchBusinessPermissions(); };
    window.addEventListener('assetViewsChanged', handleViewsChanged);
    window.addEventListener('locationViewsChanged', handleViewsChanged);

    return () => {
      window.removeEventListener('sidebarNotificationsPreferenceChanged', handlePreferenceChange as EventListener);
      window.removeEventListener('assetViewsChanged', handleViewsChanged);
      window.removeEventListener('locationViewsChanged', handleViewsChanged);
    };
  }, []);

  useEffect(() => {
    // Get business name from localStorage or from businessContext if available
    let businessNameFromStore = localStorage.getItem('businessName');
    if (!businessNameFromStore) {
      try {
        // Try to get business name from the stored business context
        const businessContextStr = localStorage.getItem('businessContext');
        if (businessContextStr) {
          const businessContext = JSON.parse(businessContextStr);
          businessNameFromStore = businessContext.name;
        }
      } catch (err) {
        console.error('Error parsing businessContext:', err);
      }
    }
    
    setBusinessName(businessNameFromStore || '');

    // Fetch business permissions
    fetchBusinessPermissions();

    // Fetch all notification counts
    fetchDvlaNotificationCount();
    fetchSafetyChecksNotificationCount();
    fetchLoanChecksNotificationCount();
    fetchIncidentsNotificationCount();
    fetchDeliveryChecksNotificationCount();
    fetchInstallationsNotificationCount();

    // Set up interval to refresh notification counts every 30 seconds
    const interval = setInterval(() => {
      fetchDvlaNotificationCount();
      fetchSafetyChecksNotificationCount();
      fetchLoanChecksNotificationCount();
      fetchIncidentsNotificationCount();
      fetchDeliveryChecksNotificationCount();
      fetchInstallationsNotificationCount();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed));
    }
  }, [sidebarCollapsed, isMobile]);

  const startOpenFlyout = (anchorEl: HTMLElement, item: MenuEntry) => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
    hoverOpenTimerRef.current = setTimeout(() => {
      setFlyout({ anchorEl, item, activeSubIdx: -1 });
      hoverOpenTimerRef.current = null;
    }, 150);
  };

  const startCloseFlyout = () => {
    if (hoverOpenTimerRef.current) {
      clearTimeout(hoverOpenTimerRef.current);
      hoverOpenTimerRef.current = null;
    }
    hoverCloseTimerRef.current = setTimeout(() => {
      setFlyout(null);
      hoverCloseTimerRef.current = null;
    }, 250);
  };

  const cancelCloseFlyout = () => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  };

  const fetchDvlaNotificationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/dvla-staff/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && Array.isArray(response.data)) {
        const unreadCount = response.data.filter((n: any) => !n.read_at).length;
        setDvlaNotificationCount(unreadCount);
      }
    } catch (error) {
      // Silently fail - don't show errors for notification count
      console.error('Error fetching DVLA notification count:', error);
    }
  };

  const fetchSafetyChecksNotificationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/notifications/platform`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success && Array.isArray(response.data.data)) {
        const unreadCount = response.data.data.filter((n: any) => !n.is_read).length;
        setSafetyChecksNotificationCount(unreadCount);
      }
    } catch (error) {
      // Silently fail - don't show errors for notification count
      console.error('Error fetching Safety Checks notification count:', error);
    }
  };

  const fetchLoanChecksNotificationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Query assets directly to count currently out of action assets
      const response = await axios.get(`${API_BASE_URL}/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Handle both response formats: wrapped in {success, data} or direct array
      let assets: any[] = [];
      if (response.data?.success && Array.isArray(response.data.data)) {
        assets = response.data.data;
      } else if (Array.isArray(response.data)) {
        assets = response.data;
      } else if (Array.isArray(response.data?.data)) {
        assets = response.data.data;
      }

      if (assets.length > 0) {
        // Count assets that are currently out of action due to loan check reasons.
        // Exclude assets marked OOA by a failed safety check — those are flagged
        // on the Safety Checks badge, not here.
        const outOfActionCount = assets.filter((asset: any) => {
          const isOutOfAction = asset.is_out_of_action;
          const isOOA = isOutOfAction === true || 
                 isOutOfAction === 1 || 
                 isOutOfAction === '1' || 
                 isOutOfAction === 'true' ||
                 isOutOfAction === 'True';
          if (!isOOA) return false;
          const note: string = asset.out_of_action_note || '';
          return !note.toLowerCase().includes('failed safety check');
        }).length;
        
        setLoanChecksNotificationCount(outOfActionCount);
      } else {
        setLoanChecksNotificationCount(0);
      }
    } catch (error) {
      // Silently fail - don't show errors for notification count
      console.error('Error fetching Loan Checks notification count:', error);
      setLoanChecksNotificationCount(0);
    }
  };

  const fetchIncidentsNotificationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/incidents/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success && response.data.data) {
        const data = response.data.data;
        const stats = data.statistics ?? data;
        // Count unresolved incidents (status not 'resolved' or 'closed')
        const total = Number(stats.total) || 0;
        const resolved = Number(stats.resolved) || 0;
        const closed = Number(stats.closed) || 0;
        const unresolvedCount = Math.max(0, total - resolved - closed);
        setIncidentsNotificationCount(unresolvedCount);
      }
    } catch (error) {
      // Silently fail - don't show errors for notification count
      console.error('Error fetching Incidents notification count:', error);
    }
  };

  const fetchDeliveryChecksNotificationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      if (!token || !businessId) return;

      // Fetch both incoming and outgoing deliveries
      const [historyResponse, outgoingResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/deliveries/history`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 1000 }
        }).catch(() => ({ data: { success: true, transfers: [] } })),
        axios.get(`${API_BASE_URL}/outgoing-deliveries/pending`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: 'pending,in_progress,completed,rejected' }
        }).catch(() => ({ data: { success: true, deliveries: [] } }))
      ]);

      let pendingAndInTransitCount = 0;

      // Count from standard delivery transfers
      if (historyResponse.data.success && Array.isArray(historyResponse.data.transfers)) {
        const pendingInTransit = historyResponse.data.transfers.filter((t: any) => 
          t.status === 'pending' || t.status === 'in_transit'
        );
        pendingAndInTransitCount += pendingInTransit.length;
      }

      // Count from outgoing delivery requests
      if (outgoingResponse.data.success && Array.isArray(outgoingResponse.data.deliveries)) {
        // Only count deliveries with check_id (delivery checks)
        const deliveriesWithChecks = outgoingResponse.data.deliveries.filter((od: any) => od.check_id);
        const pendingInTransit = deliveriesWithChecks.filter((od: any) => 
          od.request_status === 'pending' || od.request_status === 'in_progress'
        );
        pendingAndInTransitCount += pendingInTransit.length;
      }

      setDeliveryChecksNotificationCount(pendingAndInTransitCount);
    } catch (error) {
      // Silently fail - don't show errors for notification count
      console.error('Error fetching Delivery Checks notification count:', error);
    }
  };

  const fetchInstallationsNotificationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/outgoing-deliveries/installations`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: 'all' }
      });

      if (response.data?.success && Array.isArray(response.data.installations)) {
        // Count pending installations
        const pendingCount = response.data.installations.filter((installation: any) => 
          installation.installation_status === 'pending'
        ).length;
        setInstallationsNotificationCount(pendingCount);
      } else if (Array.isArray(response.data?.installations)) {
        // Handle case where response might not have success field
        const pendingCount = response.data.installations.filter((installation: any) => 
          installation.installation_status === 'pending'
        ).length;
        setInstallationsNotificationCount(pendingCount);
      }
    } catch (error) {
      // Silently fail - don't show errors for notification count
      console.error('Error fetching Installations notification count:', error);
    }
  };

  const fetchBusinessPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      
      if (!token || !businessId) {
        console.warn('No authentication token or business ID found');
        // Default to false when no business context is available
        setBusinessPermissions({
          allowHrModule: false,
          allowIncidents: false,
          allowDvla: false,
          allowSafetyChecks: false,
          allowLoanChecks: false,
          allowDeliveryChecks: false,
          allowCalendar: false,
          allowMotTaxTab: false,
          allowVehicleEvaluationTab: false,
          hideMotTaxNavWhenDisabled: false,
          hideVehicleEvaluationNavWhenDisabled: false,
          allowKeyDocuments: true,
          allowSharedAccounts: false,
          allowLocationChecks: false,
          useSiteTerminology: false,
        });
        setAdminUiLabels({});
        setPermissionsLoaded(true);
        return;
      }

      console.log('Fetching business permissions for business:', businessId);
      const response = await axios.get(`${API_BASE_URL}/businesses/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Business permissions response:', response.data);
      
      const businessData = response.data;
      setBusinessPermissions({
        // Handle database values: 1 = true, 0 = false, null/undefined = false
        allowHrModule: businessData.allow_hr_module === 1,
        allowIncidents: businessData.allow_incidents !== 0, // Default to true unless explicitly disabled
        allowDvla: businessData.allow_dvla !== 0, // Default to true unless explicitly disabled
        allowSafetyChecks: businessData.allow_safety_checks !== 0,
        allowLoanChecks: businessData.allow_loan_checks !== 0,
        allowDeliveryChecks: businessData.allow_delivery_checks !== 0,
        allowCalendar: businessData.allow_calendar !== 0,
        allowMotTaxTab: businessData.allow_mot_tax_tab === 1 || businessData.allow_mot_tax_tab === true,
        allowVehicleEvaluationTab: businessData.allow_vehicle_evaluation_tab === 1 || businessData.allow_vehicle_evaluation_tab === true,
        hideMotTaxNavWhenDisabled:
          businessData.hide_mot_tax_nav_when_disabled === 1 ||
          businessData.hide_mot_tax_nav_when_disabled === true,
        hideVehicleEvaluationNavWhenDisabled:
          businessData.hide_vehicle_evaluation_nav_when_disabled === 1 ||
          businessData.hide_vehicle_evaluation_nav_when_disabled === true,
        hideTemplates: businessData.hide_templates === 1 || businessData.hide_templates === true,
        allowKeyDocuments:
          businessData.allow_key_documents == null
            ? true
            : businessData.allow_key_documents === 1 || businessData.allow_key_documents === true,
        allowSharedAccounts:
          businessData.allow_shared_accounts === 1 || businessData.allow_shared_accounts === true,
        allowLocationChecks:
          businessData.allow_location_checks === 1 || businessData.allow_location_checks === true,
        useSiteTerminology:
          businessData.use_site_terminology === 1 || businessData.use_site_terminology === true,
      });
      const parsedLabels = parseAdminUiLabels(businessData.admin_ui_labels);
      const useSite =
        businessData.use_site_terminology === 1 || businessData.use_site_terminology === true;
      // Site terminology defaults apply only when the business has not set a custom label.
      if (useSite && !parsedLabels.nav_location_checks) {
        parsedLabels.nav_location_checks = 'Site Checks';
      }
      if (useSite && !parsedLabels.nav_locations) {
        parsedLabels.nav_locations = 'Sites';
      }
      setAdminUiLabels(parsedLabels);
      // Parse custom asset views for dynamic sidebar items
      try {
        const rawViews = businessData.asset_views;
        const parsedViews = rawViews
          ? (typeof rawViews === 'string' ? JSON.parse(rawViews) : rawViews)
          : [];
        setAssetViews(Array.isArray(parsedViews) ? parsedViews : []);
      } catch {
        setAssetViews([]);
      }
      try {
        const rawLocViews = businessData.location_views;
        const parsedLocViews = rawLocViews
          ? (typeof rawLocViews === 'string' ? JSON.parse(rawLocViews) : rawLocViews)
          : [];
        setLocationViews(Array.isArray(parsedLocViews) ? parsedLocViews : []);
      } catch {
        setLocationViews([]);
      }
      setPermissionsLoaded(true);
    } catch (error) {
      console.error('Error fetching business permissions:', error);
      // Default to false on error for security
      setBusinessPermissions({
        allowHrModule: false,
        allowIncidents: false,
        allowDvla: false,
        allowSafetyChecks: false,
        allowLoanChecks: false,
        allowDeliveryChecks: false,
        allowCalendar: false,
        allowMotTaxTab: false,
        allowVehicleEvaluationTab: false,
        hideMotTaxNavWhenDisabled: false,
        hideVehicleEvaluationNavWhenDisabled: false,
        allowKeyDocuments: true,
        allowSharedAccounts: false,
        allowLocationChecks: false,
        useSiteTerminology: false,
      });
      setAdminUiLabels({});
      setPermissionsLoaded(true);
    }
  };

  // Redirect to dashboard if at root admin path
  if (location.pathname === '/admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const isSidebarCollapsed = !isMobile && sidebarCollapsed;
  const activeDrawerWidth = isMobile ? 0 : isSidebarCollapsed ? drawerWidthCollapsed : drawerWidthExpanded;

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen((open) => !open);
  };

  const handleSidebarCollapseToggle = () => {
    setSidebarCollapsed((collapsed) => !collapsed);
  };

  const handleNavigation = (path: string) => {
    if (path.startsWith('http://') || path.startsWith('https://')) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const handleLogout = () => {
    // Clear all auth-related items from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('impersonatingBusiness');
    localStorage.removeItem('businessContext');
    localStorage.removeItem('businessName');
    localStorage.removeItem('businessId');
    localStorage.removeItem('superAdminToken');
    
    // Use window.location.href for a full page reload to ensure clean state
    window.location.href = '/login';
  };

  const getNavSections = (): NavSection[] => {
    if (!permissionsLoaded || !businessPermissions) return [];

    const sections: NavSection[] = [];

    sections.push({
      title: 'Overview',
      items: [{ id: 'nav_dashboard', icon: <DashboardIcon />, path: '/admin/dashboard', exactMatch: true }],
    });

    const operations: MenuEntry[] = [];
    if (!userRestrictions.assets) {
      operations.push({
        id: 'nav_assets',
        icon: <BuildIcon />,
        path: '/admin/assets',
        subItems: getAssetsSubItems(businessPermissions.allowKeyDocuments),
      });
      // Inject bespoke asset views as sibling sidebar items
      assetViews
        .filter(v => v.showInWeb)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .forEach(v => {
          operations.push({
            id: `asset_view_${v.id}`,
            rawLabel: v.label,
            icon: renderAssetViewIcon(v.icon),
            path: `/admin/asset-views/${v.id}`,
            exactMatch: true,
          });
        });
    }
    // Locations / sites — gated by the 'locations' user restriction
    if (!userRestrictions.locations) {
      operations.push({
        id: 'nav_locations',
        icon: <LocationOnIcon />,
        path: '/admin/locations',
        subItems: getLocationsSubItems(
          resolveAdminUiLabel('nav_locations', adminUiLabels),
          businessPermissions.useSiteTerminology,
        ),
      });
      locationViews
        .filter(v => v.showInWeb)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .forEach(v => {
          operations.push({
            id: `location_view_${v.id}`,
            rawLabel: v.label,
            icon: renderLocationViewIcon(v.icon),
            path: `/admin/location-views/${v.id}`,
            exactMatch: true,
          });
        });
    }
    if (businessPermissions.allowSafetyChecks && !userRestrictions.safety_checks) {
      operations.push({
        id: 'nav_safety_checks',
        icon: <AssignmentIcon />,
        path: '/admin/safety-checks',
        subItems: getSafetyChecksSubItems(),
      });
    }
    if (businessPermissions.allowLoanChecks && !userRestrictions.loan_checks) {
      operations.push({
        id: 'nav_loan_checks',
        icon: <HandshakeIcon />,
        path: '/admin/loan-checks',
        subItems: getLoanChecksSubItems(),
      });
    }
    if (businessPermissions.allowDeliveryChecks && !userRestrictions.delivery_checks) {
      operations.push({
        id: 'nav_delivery_checks',
        icon: <LocalShippingIcon />,
        path: '/admin/delivery-checks',
        subItems: getDeliveryChecksSubItems(),
      });
    }
    if (businessPermissions.allowLocationChecks && !userRestrictions.location_checks) {
      operations.push({
        id: 'nav_location_checks',
        icon: <AssignmentIcon />,
        path: '/admin/location-checks',
        subItems: getLocationChecksSubItems(),
      });
    }
    if (businessPermissions.allowCalendar && !userRestrictions.calendar) {
      operations.push({
        id: 'nav_calendar',
        icon: <CalendarMonthIcon />,
        path: '/admin/calendar',
        subItems: [
          { icon: <CalendarMonthIcon />, label: resolveAdminUiLabel('nav_calendar', adminUiLabels), path: '/admin/calendar' },
          { icon: <ListAltIcon />, label: resolveAdminUiLabel('nav_calendar_bookings', adminUiLabels), path: '/admin/calendar/bookings' },
          { icon: <SettingsIcon />, label: resolveAdminUiLabel('nav_calendar_settings', adminUiLabels), path: '/admin/calendar/settings' },
        ],
      });
    }
    if (!(businessPermissions as { hideTemplates?: boolean }).hideTemplates && !userRestrictions.templates) {
      operations.push({
        id: 'nav_templates',
        icon: <ListAltIcon />,
        path: '/admin/templates',
        subItems: getTemplatesSubItems(businessPermissions.allowLocationChecks, businessPermissions.useSiteTerminology, adminUiLabels),
      });
    }
    if (operations.length > 0) {
      sections.push({ title: 'Operations', items: operations });
    }

    const insightsItems: MenuEntry[] = [];
    if (!userRestrictions.communications) {
      insightsItems.push({
        id: 'nav_communications',
        icon: <CampaignIcon />,
        path: '/admin/communications',
        subItems: getCommunicationsSubItems(),
      });
    }
    if (!userRestrictions.reports) {
      insightsItems.push({ id: 'nav_reports', icon: <AssessmentIcon />, path: '/admin/reports' });
    }
    if (insightsItems.length > 0) {
      sections.push({ title: 'Insights', items: insightsItems });
    }

    const compliance: MenuEntry[] = [];
    if (businessPermissions.allowDvla && userDvlaAccessEnabled && !userRestrictions.dvla_web) {
      compliance.push({
        id: 'nav_dvla',
        icon: <ListAltIcon />,
        path: '/admin/dvla',
        subItems: getDvlaSubItems(),
      });
    }
    if (businessPermissions.allowIncidents && !userRestrictions.incidents) {
      compliance.push({ id: 'nav_incidents', icon: <ReportProblemIcon />, path: '/admin/incidents' });
    }
    if (businessPermissions.allowHrModule && !userRestrictions.hr) {
      compliance.push({ id: 'nav_hr', icon: <PeopleIcon />, path: '/admin/hr' });
    }
    if (compliance.length > 0) {
      sections.push({ title: 'People & compliance', items: compliance });
    }

    const fleet: MenuEntry[] = [];
    const showMotTaxNav =
      businessPermissions.allowMotTaxTab || !businessPermissions.hideMotTaxNavWhenDisabled;
    const showVehicleEvalNav =
      businessPermissions.allowVehicleEvaluationTab ||
      !businessPermissions.hideVehicleEvaluationNavWhenDisabled;
    if (showMotTaxNav) {
      fleet.push({ id: 'nav_mot_tax', icon: <DirectionsCarIcon />, path: '/admin/mot-tax' });
    }
    if (showVehicleEvalNav) {
      fleet.push({ id: 'nav_vehicle_evaluation', icon: <SpeedIcon />, path: '/admin/vehicle-evaluation' });
    }
    if (fleet.length > 0) {
      sections.push({ title: 'Fleet', items: fleet });
    }

    const adminItems: MenuEntry[] = [];
    if (!userRestrictions.installations) {
      adminItems.push({ id: 'nav_installations', icon: <LocalShippingIcon />, path: '/admin/installations' });
    }
    adminItems.push(
      { id: 'nav_push_notifications', icon: <NotificationsIcon />, path: '/admin/push-notifications' },
      {
        id: 'nav_settings',
        icon: <SettingsIcon />,
        path: '/admin/settings',
        subItems: getSettingsSubItems(businessPermissions.allowSharedAccounts, businessPermissions.useSiteTerminology),
      },
    );

    const TEST_BUSINESS_ID = '01d96b55-9211-40e5-a249-137787baa921';
    try {
      const bid = localStorage.getItem('businessId')?.trim() ?? '';
      const bName = (localStorage.getItem('businessName') ?? '').trim();
      if (bid === TEST_BUSINESS_ID || bName === 'Test Business') {
        adminItems.push({
          id: 'nav_documentation',
          icon: <DocumentationIcon />,
          path: '/admin/documentation',
        });
      }
    } catch (_) {
      /* ignore */
    }

    const impersonating =
      typeof window !== 'undefined' &&
      localStorage.getItem('impersonatingBusiness') === 'true';
    if (impersonating) {
      adminItems.push({
        id: 'nav_business_ui_labels',
        icon: <EditNoteIcon />,
        path: '/admin/business-ui-labels',
      });
    }

    adminItems.push({ id: 'nav_support', icon: <SupportIcon />, path: '/admin/support-tickets' });
    sections.push({ title: 'Admin', items: adminItems });

    return sections;
  };

  const navSections = getNavSections();

  const renderNavIcon = (item: MenuEntry) => {
    if (hideSidebarNotifications) return item.icon;
    if (item.id === 'nav_dvla' && dvlaNotificationCount > 0) {
      return <Badge badgeContent={dvlaNotificationCount} color="error">{item.icon}</Badge>;
    }
    if (item.id === 'nav_safety_checks' && safetyChecksNotificationCount > 0) {
      return <Badge badgeContent={safetyChecksNotificationCount} color="error">{item.icon}</Badge>;
    }
    if (item.id === 'nav_loan_checks' && loanChecksNotificationCount > 0) {
      return <Badge badgeContent={loanChecksNotificationCount} color="error">{item.icon}</Badge>;
    }
    if (item.id === 'nav_incidents' && incidentsNotificationCount > 0) {
      return <Badge badgeContent={incidentsNotificationCount} color="error">{item.icon}</Badge>;
    }
    if (item.id === 'nav_delivery_checks' && deliveryChecksNotificationCount > 0) {
      return <Badge badgeContent={deliveryChecksNotificationCount} color="error">{item.icon}</Badge>;
    }
    if (item.id === 'nav_installations' && installationsNotificationCount > 0) {
      return <Badge badgeContent={installationsNotificationCount} color="error">{item.icon}</Badge>;
    }
    return item.icon;
  };

  const renderNavItem = (item: MenuEntry) => {
    // For asset view items, match by pathname + full query param (not prefix)
    const isViewItem = item.id.startsWith('asset_view_');
    const isSelected = isViewItem
      ? location.pathname === item.path
      : isNavPathActive(location.pathname, item.path, item.exactMatch);
    const primary = item.rawLabel ?? resolveAdminUiLabel(item.id as AdminUiLabelKey, adminUiLabels);
    const isMotTax = item.id === 'nav_mot_tax';
    const isVehicleEval = item.id === 'nav_vehicle_evaluation';
    const isFeatureDisabled =
      (isMotTax && businessPermissions && !businessPermissions.allowMotTaxTab) ||
      (isVehicleEval && businessPermissions && !businessPermissions.allowVehicleEvaluationTab);
    const disabledTooltip = isMotTax
      ? 'MOT / Tax is not enabled. A super admin can enable it under Features & Access.'
      : isVehicleEval
        ? 'Vehicle Evaluation is not enabled. A super admin can enable it under Features & Access.'
        : '';

    const hoverHandlers = item.subItems?.length && !isFeatureDisabled
      ? {
          onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
            startOpenFlyout(e.currentTarget, item);
          },
          onMouseLeave: () => {
            startCloseFlyout();
          },
        }
      : {};

    // In collapsed mode render a compact centred icon button instead of a full ListItem
    if (isSidebarCollapsed) {
      const tooltipLabel = item.subItems?.length
        ? primary
        : isFeatureDisabled ? disabledTooltip : primary;

      const collapsedItem = (
        <Box
          key={item.id}
          onClick={() => {
            if (!isFeatureDisabled) handleNavigation(item.path);
          }}
          {...hoverHandlers}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            my: 0.25,
            width: 44,
            height: 40,
            borderRadius: 1.5,
            cursor: isFeatureDisabled ? 'default' : 'pointer',
            opacity: isFeatureDisabled ? 0.45 : 1,
            color: isSelected ? theme.palette.primary.contrastText : 'text.secondary',
            bgcolor: isSelected ? 'primary.main' : 'transparent',
            transition: 'all 0.15s ease',
            userSelect: 'none',
            '&:hover': isFeatureDisabled
              ? {}
              : {
                  bgcolor: isSelected ? 'primary.dark' : theme.palette.action.hover,
                  color: isSelected ? theme.palette.primary.contrastText : theme.palette.primary.main,
                },
            '& .MuiSvgIcon-root': { fontSize: '1.2rem' },
          }}
        >
          {renderNavIcon(item)}
        </Box>
      );

      return (
        <Tooltip key={item.id} title={tooltipLabel} placement="right">
          <span style={{ display: 'flex', justifyContent: 'center' }}>{collapsedItem}</span>
        </Tooltip>
      );
    }

    const listItem = (
      <ListItem
        button
        key={item.id}
        onClick={() => {
          if (!isFeatureDisabled) handleNavigation(item.path);
        }}
        {...hoverHandlers}
        selected={isSelected}
        sx={{
          mx: 1,
          my: 0.1,
          pl: item.indent ? 3.5 : 2,
          pr: 2,
          borderRadius: 1.5,
          minHeight: item.indent ? 34 : 36,
          transition: 'all 0.2s ease',
          opacity: isFeatureDisabled ? 0.45 : 1,
          cursor: isFeatureDisabled ? 'default' : 'pointer',
          userSelect: 'none',
          borderLeft: isSelected
            ? `3px solid ${theme.palette.primary.main}`
            : '3px solid transparent',
          '&:hover': isFeatureDisabled
            ? {}
            : {
                backgroundColor: theme.palette.action.hover,
                '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
              },
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '& .MuiListItemIcon-root': { color: theme.palette.primary.contrastText },
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: item.indent ? 30 : 36,
            justifyContent: 'center',
            color: isSelected ? 'inherit' : 'text.secondary',
            '& .MuiSvgIcon-root': { fontSize: '1.15rem' },
          }}
        >
          {renderNavIcon(item)}
        </ListItemIcon>
        <ListItemText
          primary={primary}
          primaryTypographyProps={{
            fontSize: item.indent ? '0.8rem' : '0.845rem',
            fontWeight: isSelected ? 600 : 500,
            fontFamily: 'Lato, sans-serif',
            noWrap: true,
          }}
        />
        {item.subItems?.length ? (
          <Tooltip title="More options" placement="right" arrow>
            <Box
              component="span"
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                const anchor = (e.currentTarget as HTMLElement).closest('li') as HTMLElement ?? e.currentTarget as HTMLElement;
                if (flyout?.item.id === item.id) {
                  cancelCloseFlyout();
                  setFlyout(null);
                } else {
                  cancelCloseFlyout();
                  if (hoverOpenTimerRef.current) {
                    clearTimeout(hoverOpenTimerRef.current);
                    hoverOpenTimerRef.current = null;
                  }
                  setFlyout({ anchorEl: anchor, item, activeSubIdx: -1 });
                }
              }}
              sx={{
                display: 'inline-flex',
                gap: '3px',
                alignItems: 'center',
                flexShrink: 0,
                ml: 0.5,
                px: 0.75,
                py: 0.75,
                borderRadius: 1,
                opacity: flyout?.item.id === item.id ? 1 : isSelected ? 0.6 : 0.3,
                transition: 'opacity 0.2s ease, background-color 0.15s ease',
                'li:hover &': { opacity: 0.85 },
                '&:hover': {
                  opacity: '1 !important',
                  bgcolor: 'action.hover',
                },
              }}
            >
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  component="span"
                  sx={{
                    width: 3,
                    height: 3,
                    borderRadius: '50%',
                    bgcolor: 'currentColor',
                  }}
                />
              ))}
            </Box>
          </Tooltip>
        ) : null}
      </ListItem>
    );

    const tooltipTitle = isFeatureDisabled ? disabledTooltip : primary;

    return (
      <Tooltip key={item.id} title={tooltipTitle} placement="right" disableHoverListener={!isSidebarCollapsed && !isFeatureDisabled}>
        <span style={{ display: 'block' }}>{listItem}</span>
      </Tooltip>
    );
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
          gap: 1.5,
          minHeight: appBarHeight,
          px: isSidebarCollapsed ? 1 : 2,
          flexShrink: 0,
        }}
      >
        {isSidebarCollapsed ? (
          <Tooltip title="Expand sidebar" placement="right">
            <Box
              onClick={handleSidebarCollapseToggle}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                cursor: 'pointer',
                borderRadius: 1.5,
                p: 0.5,
                transition: 'background-color 0.15s ease',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <img src={process.env.PUBLIC_URL + '/safety.png'} alt="Check Now Logo" style={{ width: 28, height: 28, display: 'block' }} />
            </Box>
          </Tooltip>
        ) : (
          <>
            <img src={process.env.PUBLIC_URL + '/safety.png'} alt="Check Now Logo" style={{ width: 32, height: 32, flexShrink: 0 }} />
            <BrandWordmark
              variant="h6"
              compact
              sx={{ fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}
            />
            {!isMobile && (
              <Tooltip title="Collapse sidebar" placement="right">
                <IconButton
                  onClick={handleSidebarCollapseToggle}
                  size="small"
                  aria-label="Collapse sidebar"
                  sx={{
                    ml: 'auto',
                    borderRadius: 1.5,
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Toolbar>
      <Divider />
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 0.25 }}>
        {!permissionsLoaded ? (
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CircularProgress size={22} />
              </ListItemIcon>
              <ListItemText primary="Loading menu…" primaryTypographyProps={{ fontFamily: 'Lato' }} />
            </ListItem>
          </List>
        ) : (
          navSections.map((section, sectionIndex) => (
            <List key={section.title} dense disablePadding>
              {isSidebarCollapsed ? (
                sectionIndex > 0 ? <Divider sx={{ my: 0.75, mx: 1.5 }} /> : null
              ) : (
                <ListSubheader
                  disableSticky
                  sx={{
                    lineHeight: '24px',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.disabled',
                    bgcolor: 'transparent',
                    px: 2.5,
                    pt: 1,
                    pb: 0,
                    fontFamily: 'Lato, sans-serif',
                  }}
                >
                  {section.title}
                </ListSubheader>
              )}
              {section.items.map((item) => renderNavItem(item))}
            </List>
          ))
        )}
      </Box>
      <Divider sx={{ flexShrink: 0 }} />
      <Box sx={{ flexShrink: 0, pb: 1 }}>
        {isSidebarCollapsed ? (
          <Tooltip title={resolveAdminUiLabel('nav_logout', adminUiLabels)} placement="right">
            <Box
              onClick={handleLogout}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                my: 0.25,
                width: 44,
                height: 40,
                borderRadius: 1.5,
                cursor: 'pointer',
                color: 'text.secondary',
                '& .MuiSvgIcon-root': { fontSize: '1.2rem' },
                '&:hover': {
                  bgcolor: theme.palette.error.light,
                  color: theme.palette.error.contrastText,
                },
              }}
            >
              <LogoutIcon />
            </Box>
          </Tooltip>
        ) : (
          <List dense>
            <ListItem
              button
              onClick={handleLogout}
              sx={{
                mx: 1,
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: theme.palette.error.light,
                  color: theme.palette.error.contrastText,
                  '& .MuiListItemIcon-root': { color: theme.palette.error.contrastText },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, justifyContent: 'center' }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText
                primary={resolveAdminUiLabel('nav_logout', adminUiLabels)}
                sx={{ '& .MuiTypography-root': { fontFamily: 'Lato' } }}
              />
            </ListItem>
          </List>
        )}
      </Box>
    </Box>
  );

  const flyoutPopper = (
    <Popper
      open={Boolean(flyout)}
      anchorEl={flyout?.anchorEl ?? null}
      placement="right-start"
      transition
      style={{ zIndex: theme.zIndex.drawer + 200 }}
      modifiers={[{ name: 'offset', options: { offset: [0, 10] } }]}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={160}>
          <Paper
            elevation={0}
            onMouseEnter={cancelCloseFlyout}
            onMouseLeave={startCloseFlyout}
            sx={{
              p: 0.75,
              borderRadius: 3,
              bgcolor: 'background.paper',
              boxShadow:
                '0 20px 48px rgba(0,0,0,0.14), 0 6px 16px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.06)',
              minWidth: 230,
              overflow: 'hidden',
              userSelect: 'none',
            }}
          >
            {/* Section header */}
            <Box sx={{ px: 1.5, pt: 1.25, pb: 0.75 }}>
              <Typography
                sx={{
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  letterSpacing: '0.09em',
                  textTransform: 'uppercase',
                  color: 'text.disabled',
                  fontFamily: 'Lato, sans-serif',
                }}
              >
                {flyout ? (flyout.item.rawLabel ?? resolveAdminUiLabel(flyout.item.id as AdminUiLabelKey, adminUiLabels)) : ''}
              </Typography>
            </Box>
            <Divider sx={{ mx: 0.5, mb: 0.5 }} />

            {/* Sub-items */}
            {flyout?.item.subItems?.map((subItem, idx) => {
              const isActive = flyout.activeSubIdx === idx;
              return (
                <Box
                  key={subItem.path}
                  onMouseEnter={() =>
                    setFlyout((prev) => (prev ? { ...prev, activeSubIdx: idx } : prev))
                  }
                  onMouseLeave={() =>
                    setFlyout((prev) => (prev ? { ...prev, activeSubIdx: -1 } : prev))
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNavigation(subItem.path);
                    setFlyout(null);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 0.875,
                    mx: 0.25,
                    my: 0.2,
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'background-color 0.12s ease, color 0.12s ease',
                    bgcolor: isActive ? 'primary.main' : 'transparent',
                    color: isActive ? 'primary.contrastText' : 'text.primary',
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.1rem',
                      color: isActive ? 'primary.contrastText' : 'text.secondary',
                      transition: 'color 0.12s ease',
                      flexShrink: 0,
                    },
                  }}
                >
                  {subItem.icon}
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: isActive ? 600 : 500,
                      fontFamily: 'Lato, sans-serif',
                      lineHeight: 1.3,
                      flexGrow: 1,
                    }}
                  >
                    {subItem.label}
                  </Typography>
                </Box>
              );
            })}

          </Paper>
        </Fade>
      )}
    </Popper>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', overflow: 'hidden' }}>
      <AppUpdateBanner />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${activeDrawerWidth}px)` },
          ml: { sm: `${activeDrawerWidth}px` },
          zIndex: (t) => t.zIndex.drawer + 1,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: 'none',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          transition: drawerTransition(theme),
        }}
      >
        <Toolbar
          sx={{
            minHeight: appBarHeight,
            px: { xs: 2, sm: 3 },
            gap: 1,
          }}
        >
          {/* Mobile: hamburger to open temporary drawer */}
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleMobileDrawerToggle}
            sx={{
              mr: 0.5,
              display: { xs: 'flex', sm: 'none' },
              borderRadius: 1.5,
              color: 'text.secondary',
              '& .MuiSvgIcon-root': { fontSize: '1.15rem' },
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.primary.main,
              },
            }}
          >
            <MenuIcon />
          </IconButton>
          {/* Desktop: expand button — only visible when sidebar is collapsed */}
          {isSidebarCollapsed && (
            <Tooltip title="Expand sidebar" placement="right">
              <IconButton
                aria-label="expand sidebar"
                edge="start"
                onClick={handleSidebarCollapseToggle}
                sx={{
                  mr: 0.5,
                  display: { xs: 'none', sm: 'flex' },
                  borderRadius: 1.5,
                  color: 'text.secondary',
                  '& .MuiSvgIcon-root': { fontSize: '1.15rem' },
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}
          <Box sx={{ flexGrow: 1, minWidth: 0, py: 0.25 }}>
            {businessName ? (
              <>
                <Typography
                  noWrap
                  sx={{
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    lineHeight: 1.25,
                    color: 'text.primary',
                  }}
                >
                  {businessName}
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    lineHeight: 1.3,
                    color: 'text.disabled',
                  }}
                >
                  {resolveAdminUiLabel('admin_header_subtitle', adminUiLabels)}
                </Typography>
              </>
            ) : (
              <Typography
                noWrap
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {resolveAdminUiLabel('admin_header_subtitle', adminUiLabels)}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <IconButton
              aria-label="Profile"
              onClick={() => navigate('/admin/user-profile')}
              sx={{
                display: { xs: 'flex', sm: 'none' },
                ...navItemButtonSx(theme),
              }}
            >
              <PersonIcon />
            </IconButton>
            <Button
              startIcon={<PersonIcon />}
              onClick={() => navigate('/admin/user-profile')}
              sx={{
                display: { xs: 'none', sm: 'flex' },
                ...navItemButtonSx(theme),
                '& .MuiButton-startIcon': { mr: 0.75, ml: 0 },
              }}
            >
              {resolveAdminUiLabel('nav_profile', adminUiLabels)}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{
          width: { sm: activeDrawerWidth },
          flexShrink: { sm: 0 },
          position: { sm: 'fixed' },
          height: '100%',
          zIndex: theme.zIndex.drawer,
          transition: drawerTransition(theme),
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={isMobile && mobileDrawerOpen}
          onClose={handleMobileDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidthExpanded, height: '100%' },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer - always visible, collapsible */}
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: activeDrawerWidth,
              height: '100%',
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
              overflowX: 'hidden',
              transition: drawerTransition(theme),
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Main drawerWidth={activeDrawerWidth}>
        <Toolbar sx={{ minHeight: appBarHeight }} />
        <Box sx={{ width: '100%', maxWidth: '100%' }}>
          <AdminUiLabelsProvider value={{ adminUiLabels }}>
            <Outlet />
          </AdminUiLabelsProvider>
        </Box>
      </Main>
      {flyoutPopper}
    </Box>
  );
};

export default AdminLayout; 